import { axios } from '@/plugins/axios';
import { strictEntries } from '@/shared/common-function';
import { SortType } from '@/shared/type';
import { endPoints, Params } from '@/shared/types/api';
import { toRequestSchoolKey } from '@/utils/transform';
import { reactive } from '@vue/composition-api';
import { DataOptions } from 'vuetify';
import { useApi } from './useApi';
import { download as downloadFile } from '@/utils/fileDownload';
export type TableOptions = {
  sortBy: SortType | null;
  orderBy: boolean | undefined;
};

type Pagination = {
  page: number;
  itemsPerPage: number;
};

export type Response<T> = { items: T[]; total: number };

export const useTable = <
  Item,
  ResponseItem,
  FilterType extends Record<string, any>,
>({
  fetch,
  initialFilterFactory,
  download,
}: {
  fetch: (query: { [k: string]: unknown }) => Promise<Response<ResponseItem>>;
  initialFilterFactory: () => FilterType;
  download?: (query: { [k: string]: unknown }) => Promise<void>;
}) => {
  const state = reactive<{
    filter: FilterType;
    selected: Item[];
  }>({
    filter: initialFilterFactory(),
    selected: [],
  });

  const filterToQuery = (filter: FilterType) => {
    const queryArray = strictEntries(filter)
      .reduce<[string, unknown][]>((prev, [, item]) => {
        if (!item) return prev;

        if (item.rangeProps) {
          prev.push([item.name, item.value]);
          prev.push([item.rangeProps.name, item.rangeProps.value]);
        } else if (typeof item.value === 'boolean') {
          if (item.value) {
            prev.push([item.name, 1]);
          }
        } else {
          prev.push([item.name, item.value]);
        }

        return prev;
      }, [])
      .filter(([, value]) => value !== undefined);

    const queryObject = Object.fromEntries(queryArray);

    return queryObject;
  };

  const tableOptions = reactive<TableOptions>({
    sortBy: null,
    orderBy: undefined,
  });

  const pagination = reactive<Pagination>({
    page: 1,
    itemsPerPage: 10,
  });

  const updateOptions = (options: DataOptions) => {
    const {
      page,
      itemsPerPage,
      sortBy: [sortBy],
      sortDesc: [sortDesc],
    } = options;
    pagination.page = page;
    pagination.itemsPerPage = itemsPerPage;
    tableOptions.sortBy = sortBy as SortType;
    tableOptions.orderBy = sortDesc;
  };

  // APIのプロパティに変換する
  const tableOptionsToQuery = () => {
    if (tableOptions.sortBy) {
      return {
        sort_key: toRequestSchoolKey(tableOptions.sortBy),
        order_by: tableOptions.orderBy === true ? 'desc' : 'asc',
      };
    } else {
      return {};
    }
  };

  const createQuery = (filter: FilterType) => {
    return {
      ...filterToQuery(filter),
      ...tableOptionsToQuery(),
    };
  };

  const { isLoading, handleApi } = useApi<Response<ResponseItem>>(
    async (filter: FilterType) => {
      return fetch(createQuery(filter));
    },
  );

  const downloadList = async (filter: FilterType) => {
    if (!download) return;
    const query = createQuery(filter);

    return download(query);
  };

  type DocumentType = 'officialDocument' | 'resultPublication';

  const downloadPDF = async (
    type: DocumentType,
    organizationId: string,
    publishType?: number,
  ) => {
    if (type === 'officialDocument') {
      const params: Params<'downloadOfficialDocumentForAdmin'> = {
        organization_ids: [organizationId],
      };
      return axios
        .get(endPoints.downloadOfficialDocumentForAdmin.endPoint, {
          params,
          responseType: 'blob',
        })
        .then(downloadFile);
    } else if (type === 'resultPublication') {
      if (!publishType) throw new Error('publishType is required');

      const params: Params<'downloadResultPublicationDocumentForAdmin'> = {
        publish_types: publishType,
        organization_ids: [organizationId],
      };
      return axios
        .get(endPoints.downloadResultPublicationDocumentForAdmin.endPoint, {
          params,
          responseType: 'blob',
        })
        .then(downloadFile);
    }
  };

  return {
    fetchTableData: (filter: FilterType) => handleApi(filter),
    tableOptions,
    pagination,
    updateOptions,
    isLoading,
    tableState: state,
    downloadList,
    downloadPDF,
  };
};
